import { EcoaContent } from "@components/common/EconContent";
import { HbButton } from "@components/legacy/HbButton";
import { EcoaAuthenticateCodeFlax } from "@maven-rest/cdms/ecoa/EcoaAuthenticateCode.flax";
import { StudySmsFlax } from "@maven-rest/cdms/study/StudySms.flax";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { notify, useRequest } from "@coralblack/flax";
import { Footer } from "@layouts/Footer";
import { MavenService, TwoFactorAuthType } from "maven-lib/dist/dto/entity";
import { sessionSlice } from "maven-lib/dist/states";
import { ENCRYPTED_ACCESS_CODE_KEY } from "./models/identityVerification.const";
import { IDENTITY_VERIFICATION_LANG } from "./models/identityVerification.msg";
import { HbCodeInput } from "@maven-surface/components/base/HbCodeInput";
import { HbDivider } from "@maven-surface/components/base/HbDivider";
import { HbParagraph } from "@maven-surface/components/base/HbParagraph";
import { CDMS_STUDY_SUBJECT_CODE } from "@maven-msg/code/cdms";
import { CDMS_STUDY_STATUS_CODE } from "@maven-msg/code/cdms/studyStatus";
import { ECOA_ACCESS_CODE_CODE } from "@maven-msg/code/ecoaAccessCode";
import { ECOA_SUBJECT_ACCESS_CODE_LANG } from "@maven-msg/lang/ecoa";
import { ECOA_ACCESS_CODE_LANG } from "@maven-msg/lang/ecoaAccessCode";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

const SMS_CODE_LENGTH = 6;

enum SmsSendGuideStatus {
  INITIAL = "INITIAL", // 마운트 직후 / getAlive 호출 전
  SENT = "SENT", // 발송 성공, contact 표시
  SEND_ALREADY = "SEND_ALREADY", // 유효 OTP 이미 존재
  SENDING = "SENDING", // 발송 요청 중 (contact 없고 sent 플로우)
  SEND_FAILED = "SEND_FAILED", // 발송 실패 (일반)
  SEND_LOCKED = "SEND_LOCKED", // send SMS 429 / 잠금
}

interface IdentityVerificationBySmsProps {}

export function IdentityVerificationBySms(props: IdentityVerificationBySmsProps) {
  const dispatch = useDispatch();

  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const [smsCode, setSmsCode] = useState<string>("");
  const [contact, setContact] = useState<string>("");
  const [smsSendGuideStatus, setSmsSendGuideStatus] = useState<SmsSendGuideStatus>(SmsSendGuideStatus.INITIAL);

  const encryptedAccessCode = sessionStorage.getItem(ENCRYPTED_ACCESS_CODE_KEY) ?? "";

  const { request: requestToSendSms } = useRequest(
    { ...StudySmsFlax.sendEcoaAuthenticateSms({ encryptedAccessCode, service: MavenService.MavenECoa }) },
    {
      success: (data) => {
        if (data?.subjectContact) {
          setContact(data?.subjectContact.slice(-4));
          setSmsSendGuideStatus(SmsSendGuideStatus.SENT);

          return MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_SEND, { id: data?.subjectSmsId || "null" });
        } else {
          setSmsSendGuideStatus(SmsSendGuideStatus.SEND_FAILED);

          return MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_SEND_FAIL_TEXT_LABEL);
        }
      },
      error: (error: any) => {
        if (error?.code === "ECOA_AUTH_LOCKED") {
          setSmsSendGuideStatus(SmsSendGuideStatus.SEND_LOCKED);

          return MvnMessage(IDENTITY_VERIFICATION_LANG.IDENTITY_VERIFICATION_CODE_SEND_ALREADY);
        } else if (error?.code === ECOA_ACCESS_CODE_CODE.ECOA_AUTH_SMS_ALREADY_RESEND.code) {
          setSmsSendGuideStatus(SmsSendGuideStatus.SEND_ALREADY);

          return MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_ALREADY_RESEND_TEXT);
        }

        setSmsSendGuideStatus(SmsSendGuideStatus.SEND_FAILED);

        if (error?.code === CDMS_STUDY_SUBJECT_CODE.CDMS_SUBJECT_CONTACT_NOT_AVAILABLE.code)
          return MvnMessage(ECOA_SUBJECT_ACCESS_CODE_LANG.ECOA_SUBJECT_CONTACT_INVALID);

        return MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_SEND_FAIL, { message: error?.message || "An error has occurred." });
      },
    }
  );

  const { request: requestSms } = useRequest(
    { ...EcoaAuthenticateCodeFlax.getAliveEcoaAuthenticateCodeSms({ encryptedAccessCode }) },
    {
      success: (resp) => {
        const isAlive = resp.isAliveAuthenticateCodeSms;

        if (isAlive) {
          setSmsSendGuideStatus(SmsSendGuideStatus.SEND_ALREADY);

          return;
        }

        setSmsSendGuideStatus(SmsSendGuideStatus.SENDING);

        requestToSendSms({
          data: {
            encryptedAccessCode: encryptedAccessCode,
            service: MavenService.MavenECoa,
          },
        });
      },
      error: (error) => `An error has occurred. (${error.message})`,
    }
  );

  const { request: requestVerifyEcoaAuthenticateCode } = useRequest(
    {
      ...EcoaAuthenticateCodeFlax.verifyEcoaAuthenticateCode({
        encryptedAccessCode,
        twoFactorAuthType: TwoFactorAuthType.Sms,
        twoFactorAuthValue: smsCode,
      }),
    },
    {
      success: (resp) => {
        sessionStorage.removeItem(ENCRYPTED_ACCESS_CODE_KEY);
        dispatch(sessionSlice.actions.signIn({ ...resp, redirectTo: "/pat/visits" }));
      },
      error: (error) => {
        if (error?.code === CDMS_STUDY_STATUS_CODE.STUDY_CLOSED.code) {
          dispatch(sessionSlice.actions.signOut());
        }

        return MvnMessage(IDENTITY_VERIFICATION_LANG.IDENTITY_VERIFICATION_ERROR_MESSAGE);
      },
    }
  );

  function handleCodeChange(code: string) {
    setSmsCode(code);
  }

  function handleCodeFilledUp() {
    const inputs = nextButtonRef?.current?.parentElement?.getElementsByTagName("button");
    if (!inputs?.length) return;
    inputs[0]?.focus();
  }

  const resendSms = () => {
    requestToSendSms({
      data: {
        encryptedAccessCode: encryptedAccessCode,
        service: MavenService.MavenECoa,
      },
    });
  };

  function handleNextButtonClick(): void {
    if (smsCode.trim().length === SMS_CODE_LENGTH) {
      requestVerifyEcoaAuthenticateCode();
    } else {
      notify(MvnMessage(IDENTITY_VERIFICATION_LANG.IDENTITY_VERIFICATION_SMS_CODE_REQUIRED), "WARN");
    }
  }

  function getSmsGuideText(smsSendGuideStatus: SmsSendGuideStatus): string {
    if (smsSendGuideStatus === SmsSendGuideStatus.INITIAL) return "";
    if (smsSendGuideStatus === SmsSendGuideStatus.SEND_ALREADY || smsSendGuideStatus === SmsSendGuideStatus.SEND_LOCKED)
      return MvnMessage(IDENTITY_VERIFICATION_LANG.IDENTITY_VERIFICATION_CODE_SEND_ALREADY);
    if (smsSendGuideStatus === SmsSendGuideStatus.SENT) return MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_TEXT_LABEL, { contact });
    if (smsSendGuideStatus === SmsSendGuideStatus.SENDING) return MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_SENDING);
    if (smsSendGuideStatus === SmsSendGuideStatus.SEND_FAILED) return MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_SEND_FAIL_TEXT_LABEL);
    return "";
  }

  useEffect(() => {
    requestSms({ query: { encryptedAccessCode } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <EcoaContent className="app-ecoa-page-home-code-verification" verticalAlign="CENTER">
        <HbParagraph align="CENTER" margin={2} color="GRAY" text={getSmsGuideText(smsSendGuideStatus)} />
        <HbDivider space={1} />
        <HbParagraph bold={true} align="CENTER">
          {MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_PLACEHOLDER)}
        </HbParagraph>
        <HbCodeInput length={SMS_CODE_LENGTH} code={smsCode} onValueChange={handleCodeChange} onInputCompleted={handleCodeFilledUp} />
        <HbButton
          text={MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_REQUEST_RESEND_TEXT)}
          color={"TRANSPARENT"}
          useTextUnderline
          onClick={resendSms}
        ></HbButton>

        <HbDivider space={1} />
        <HbButton
          text={MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_INPUT_RESET_TEXT)}
          color="PRIMARY"
          disabled={!smsCode?.trim().length}
          onClick={() => {
            setSmsCode("");
          }}
        ></HbButton>
      </EcoaContent>
      <Footer>
        <HbButton
          elementRef={nextButtonRef}
          iconRight="ChromeBackMirrored"
          text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_NEXT)}
          onClick={handleNextButtonClick}
        />
      </Footer>
    </>
  );
}
